/**
 * User validators - User profile and authentication related types
 */
import { Infer, v } from 'convex/values';

// ============================================================================
// User Role
// ============================================================================

export const userRoleValidator = v.union(
  v.literal('substitute'),
  v.literal('provider'),
  v.literal('operator'),
  v.literal('admin')
);

export type UserRole = Infer<typeof userRoleValidator>;

export const USER_ROLES = ['substitute', 'provider', 'operator', 'admin'] as const;

// ============================================================================
// Verification Status
// ============================================================================

export const verificationStatusValidator = v.union(
  v.literal('pending'),
  v.literal('verified'),
  v.literal('rejected'),
  v.literal('changes_requested'),
  v.literal('expired_insurance'),
  v.literal('unapproved')
);

export type VerificationStatus = Infer<typeof verificationStatusValidator>;

export const VERIFICATION_STATUSES = [
  'pending',
  'verified',
  'rejected',
  'changes_requested',
  'expired_insurance',
  'unapproved',
] as const;

// ============================================================================
// Mollie Onboarding Status
// ============================================================================

export const mollieOnboardingStatusValidator = v.union(
  v.literal('not_started'),
  v.literal('in_progress'),
  v.literal('completed'),
  v.literal('needs_data'),
  v.literal('in_review')
);

export type MollieOnboardingStatus = Infer<typeof mollieOnboardingStatusValidator>;

export const MOLLIE_ONBOARDING_STATUSES = [
  'not_started',
  'in_progress',
  'completed',
  'needs_data',
  'in_review',
] as const;

// ============================================================================
// Payment Method
// ============================================================================

export const paymentMethodValidator = v.union(
  v.literal('mollie_connect'),
  v.literal('bank_transfer')
);

export type PaymentMethod = Infer<typeof paymentMethodValidator>;

export const PAYMENT_METHODS = ['mollie_connect', 'bank_transfer'] as const;

// ============================================================================
// Contract Preference
// ============================================================================

export const contractPreferenceValidator = v.union(v.literal('system'), v.literal('custom'));

export type ContractPreference = Infer<typeof contractPreferenceValidator>;

export const CONTRACT_PREFERENCES = ['system', 'custom'] as const;

// ============================================================================
// Search Mode (Service Area Preferences)
// ============================================================================

export const searchModeValidator = v.union(
  v.literal('kv'), // By KV region
  v.literal('distance') // By distance from user's home location
);

export type SearchMode = Infer<typeof searchModeValidator>;

export const SEARCH_MODES = ['kv', 'distance'] as const;

// ============================================================================
// SEPA Mandate Status
// ============================================================================

export const sepaMandateStatusValidator = v.union(
  v.literal('not_required'),
  v.literal('generated'),
  v.literal('uploaded'),
  v.literal('verified')
);

export type SepaMandateStatus = Infer<typeof sepaMandateStatusValidator>;

export const SEPA_MANDATE_STATUSES = ['not_required', 'generated', 'uploaded', 'verified'] as const;
